import { SupportedLocale } from '@app/core/locale';
import { getSuffixByUnit, Unit } from '@app/shared/models/unit';

export enum MeasureBlockConstraint {
    GREATER_THAN_0 = 'greaterThan0',
    EXACTLY_100_PERCENT = 'exactly100Percent',
    MAX_100_PERCENT = 'max100Percent',
}

// Keep in sync with the API
const MEASURE_BLOCK_CONSTRAINTS_WITH_UPPER_BOUND = [
    MeasureBlockConstraint.MAX_100_PERCENT,
    MeasureBlockConstraint.EXACTLY_100_PERCENT,
];

export function isMeasureBlockConstraintWithUpperBound(constraint: MeasureBlockConstraint): boolean {
    return MEASURE_BLOCK_CONSTRAINTS_WITH_UPPER_BOUND.includes(constraint);
}

function createGreaterThan0ConstraintMessage(unit: Unit, locale: SupportedLocale): string {
    switch (unit) {
        case Unit.BOOLEAN:
            return $localize`:@@measureBlockConstraint.greaterThan0.boolean:Es muss mindestens eine Maßnahme mit "Ja" beantwortet werden.`;

        default:
            return $localize`:@@measureBlockConstraint.greaterThan0.default:Die Summe der Eingaben muss größer als ${renderValue(0, unit, locale)}:INTERPOLATION: sein.`;
    }
}

function createMax100PercentConstraintMessage(unit: Unit, referenceAmount: number, locale: SupportedLocale): string {
    switch (unit) {
        case Unit.BOOLEAN:
            return $localize`:@@measureBlockConstraint.max100Percent.boolean:Es darf höchstens eine Maßnahme mit "Ja" beantwortet werden.`;

        default:
            return $localize`:@@measureBlockConstraint.max100Percent.default:Die Summe der Eingaben darf maximal ${renderValue(referenceAmount, unit, locale)}:INTERPOLATION: ergeben.`;
    }
}

function createExactly100PercentConstraintMessage(unit: Unit, referenceAmount: number, locale: SupportedLocale): string {
    switch (unit) {
        case Unit.BOOLEAN:
            return $localize`:@@measureBlockConstraint.exactly100Percent.boolean:Es muss genau eine zutreffende Maßnahme mit "Ja" beantwortet werden.`;

        default:
            return $localize`:@@measureBlockConstraint.exactly100Percent.default:Die Summe der Eingaben muss ${renderValue(referenceAmount, unit, locale)}:INTERPOLATION: ergeben.`;
    }
}

function renderValue(value: number, unit: Unit, locale: SupportedLocale): string {
    return withSuffix(
        convertNumber(value, unit),
        unit,
        locale,
    );
}

function convertNumber(value: number, unit: Unit): number {
    if (Unit.PERCENT === unit) {
        return value * 100;
    }

    return value;
}

function withSuffix(value: number, unit: Unit, locale: SupportedLocale): string {
    return `${value.toLocaleString(locale)}${getSuffixByUnit(unit)}`;
}

export function getMessageByMeasureBlockConstraintAndUnit(
    constraint: MeasureBlockConstraint | null,
    unit: Unit,
    referenceAmount: number,
    locale: SupportedLocale,
): string | null {
    if (null === constraint) {
        return null;
    }

    switch (constraint) {
        case MeasureBlockConstraint.GREATER_THAN_0:
            return createGreaterThan0ConstraintMessage(unit, locale);

        case MeasureBlockConstraint.EXACTLY_100_PERCENT:
            return createExactly100PercentConstraintMessage(unit, referenceAmount, locale);

        case MeasureBlockConstraint.MAX_100_PERCENT:
            return createMax100PercentConstraintMessage(unit, referenceAmount, locale);
    }
}
